
import { PowerBICourse } from './types';

export const POWERBI_PROFESSIONAL_COURSE: PowerBICourse = {
  id: 'powerbi-professional',
  title: 'Power BI Professional: Enterprise Architecture',
  description: 'Expert-level Power BI skills for enterprise deployment, governance, and advanced analytics architecture.',
  level: 'professional',
  totalDuration: '18-20 hours',
  image: '/api/placeholder/400/300',
  prerequisites: [
    'Completed Power BI Intermediate course',
    'Experience with enterprise data architecture',
    'Understanding of DevOps and CI/CD principles',
    'Knowledge of Azure services and cloud architecture'
  ],
  learningOutcomes: [
    'Design enterprise-scale Power BI architectures',
    'Implement comprehensive data governance frameworks',
    'Build automated deployment and monitoring systems',
    'Create advanced analytics solutions with AI integration',
    'Optimize performance for large-scale deployments',
    'Lead Power BI center of excellence initiatives'
  ],
  modules: [
    {
      id: 'enterprise-architecture',
      title: 'Enterprise Power BI Architecture',
      description: 'Design scalable, secure, and governed Power BI solutions for large organizations.',
      content: `
# Enterprise Power BI Architecture

## Enterprise Architecture Principles

### Scalability Design Patterns

#### Hub and Spoke Model
The hub and spoke architecture centralizes core data assets while enabling departmental flexibility.

\`\`\`
Core Data Hub (Premium Workspace)
├── Certified Datasets
│   ├── Sales Dataset (Gold Standard)
│   ├── Finance Dataset (Certified)
│   └── HR Dataset (Governed)
├── Shared Measures and Calculations
└── Common Date Tables

Department Spokes (Pro Workspaces)
├── Sales Department
│   ├── Live Connection to Sales Dataset
│   ├── Department-specific Reports
│   └── Local Calculations
├── Marketing Department
│   ├── Composite Models
│   ├── Campaign Analysis
│   └── Customer Segmentation
└── Finance Department
    ├── Financial Reports
    ├── Budget vs Actual
    └── Regulatory Reporting
\`\`\`

#### Multi-Tenant Architecture
\`\`\`yaml
Tenant Structure:
  Corporate Level:
    - Global Standards
    - Master Data Management
    - Cross-tenant Analytics
  
  Regional Level:
    - Region-specific Datasets
    - Local Compliance Requirements
    - Regional Dashboards
  
  Departmental Level:
    - Department Analytics
    - Team Dashboards
    - Individual Reports
\`\`\`

### Data Governance Framework

#### Dataset Certification Process
\`\`\`json
{
  "certificationCriteria": {
    "dataQuality": {
      "completeness": "> 95%",
      "accuracy": "Validated against source",
      "consistency": "Cross-system reconciliation",
      "timeliness": "SLA compliance"
    },
    "documentation": {
      "dataLineage": "Fully documented",
      "businessGlossary": "Terms defined",
      "updateSchedule": "Documented and followed",
      "contactInformation": "Data steward assigned"
    },
    "security": {
      "accessControls": "Role-based security",
      "sensitivityLabels": "Applied appropriately",
      "auditTrail": "Complete logging",
      "compliance": "Regulatory requirements met"
    }
  }
}
\`\`\`

#### Content Lifecycle Management
\`\`\`
Development → Testing → Staging → Production

Development Environment:
- Sandbox for experimentation
- Limited data access
- Rapid iteration capabilities

Testing Environment:
- Full data volume testing
- Performance validation
- User acceptance testing

Staging Environment:
- Production-like environment
- Final integration testing
- Change validation

Production Environment:
- Live business reports
- Full monitoring and alerting
- Strict change control
\`\`\`

### Premium Capacity Planning

#### Capacity Sizing Guidelines
\`\`\`python
# Capacity planning calculation
def calculate_capacity_requirements(
    users_count,
    datasets_count,
    avg_dataset_size_gb,
    refresh_frequency_hours,
    peak_concurrent_users_ratio=0.3
):
    
    # Base capacity requirements
    base_capacity = users_count * 0.1  # 0.1 vCores per user baseline
    
    # Dataset storage and processing
    storage_requirement = datasets_count * avg_dataset_size_gb * 1.5  # 1.5x for overhead
    processing_requirement = (datasets_count * avg_dataset_size_gb) / refresh_frequency_hours
    
    # Peak load considerations
    peak_load_factor = peak_concurrent_users_ratio * users_count * 0.05
    
    # Total capacity recommendation
    total_capacity = base_capacity + processing_requirement + peak_load_factor
    
    return {
        "recommended_sku": get_sku_recommendation(total_capacity),
        "storage_gb": storage_requirement,
        "estimated_cost_monthly": calculate_cost(total_capacity),
        "scaling_recommendations": generate_scaling_plan(total_capacity)
    }

def get_sku_recommendation(capacity_requirement):
    sku_mapping = {
        "P1": {"capacity": 8, "price": 5000},
        "P2": {"capacity": 16, "price": 10000},
        "P3": {"capacity": 32, "price": 20000},
        "P4": {"capacity": 64, "price": 40000},
        "P5": {"capacity": 128, "price": 80000}
    }
    
    for sku, specs in sku_mapping.items():
        if capacity_requirement <= specs["capacity"]:
            return sku
    return "P5+"  # Requires multiple capacities
\`\`\`

### Advanced Security Architecture

#### Row-Level Security (RLS) Implementation
\`\`\`dax
-- Dynamic security based on user context
Sales Security = 
VAR CurrentUser = USERPRINCIPALNAME()
VAR UserRegion = 
    LOOKUPVALUE(
        UserMapping[Region],
        UserMapping[Email],
        CurrentUser
    )
RETURN
    IF(
        ISBLANK(UserRegion),
        FALSE(),
        Sales[Region] = UserRegion
    )

-- Hierarchical security for managers
Manager Security = 
VAR CurrentUser = USERPRINCIPALNAME()
VAR UserLevel = 
    LOOKUPVALUE(
        UserMapping[Level],
        UserMapping[Email],
        CurrentUser
    )
VAR UserTeam = 
    LOOKUPVALUE(
        UserMapping[Team],
        UserMapping[Email],
        CurrentUser
    )
RETURN
    SWITCH(
        UserLevel,
        "Executive", TRUE(),
        "Manager", Sales[Team] = UserTeam,
        "Analyst", Sales[Analyst] = CurrentUser,
        FALSE()
    )
\`\`\`

#### Object-Level Security (OLS)
\`\`\`json
{
  "objectLevelSecurity": {
    "tables": {
      "FinancialData": {
        "allowedRoles": ["Finance", "Executive"],
        "denyRoles": ["Sales", "Marketing"]
      },
      "EmployeeData": {
        "allowedRoles": ["HR", "Executive"],
        "columnSecurity": {
          "Salary": ["HR", "Executive"],
          "Performance": ["HR", "Manager", "Executive"]
        }
      }
    },
    "measures": {
      "ProfitMargin": {
        "allowedRoles": ["Finance", "Executive"]
      },
      "CustomerLifetimeValue": {
        "allowedRoles": ["Sales", "Marketing", "Executive"]
      }
    }
  }
}
\`\`\`

### Performance Optimization Strategies

#### Query Performance Monitoring
\`\`\`sql
-- Performance monitoring DAX query
EVALUATE
ADDCOLUMNS(
    CROSSJOIN(
        VALUES(PerformanceLog[QueryType]),
        VALUES(PerformanceLog[Date])
    ),
    "AvgDuration", 
    CALCULATE(
        AVERAGE(PerformanceLog[Duration])
    ),
    "MaxDuration",
    CALCULATE(
        MAX(PerformanceLog[Duration])
    ),
    "QueryCount",
    CALCULATE(
        COUNTROWS(PerformanceLog)
    )
)
ORDER BY [Date] DESC, [AvgDuration] DESC
\`\`\`

#### Aggregation Strategy
\`\`\`dax
-- Automatic aggregations for performance
Sales Aggregations = {
    ("Year", "ProductCategory", "SUM", "Revenue"),
    ("Year", "Month", "ProductCategory", "SUM", "Revenue"),
    ("Year", "Quarter", "Region", "SUM", "Revenue"),
    ("CustomerSegment", "ProductCategory", "SUM", "Revenue")
}

-- Aggregation coverage analysis
Aggregation Coverage = 
VAR TotalQueries = COUNTROWS(QueryLog)
VAR AggregationHits = 
    COUNTROWS(
        FILTER(
            QueryLog,
            QueryLog[UsedAggregation] = TRUE()
        )
    )
RETURN
    DIVIDE(AggregationHits, TotalQueries)
\`\`\`

### Deployment Automation

#### Azure DevOps Pipeline for Power BI
\`\`\`yaml
# azure-pipelines.yml
trigger:
  branches:
    include:
    - main
    - develop

pool:
  vmImage: 'windows-latest'

variables:
  - group: PowerBI-Variables

stages:
- stage: Build
  jobs:
  - job: ValidateReports
    steps:
    - task: PowerShell@2
      displayName: 'Install Power BI PowerShell Module'
      inputs:
        script: |
          Install-Module -Name MicrosoftPowerBIMgmt -Force -Scope CurrentUser
    
    - task: PowerShell@2
      displayName: 'Validate PBIX Files'
      inputs:
        script: |
          $pbixFiles = Get-ChildItem -Path "$(Build.SourcesDirectory)" -Filter "*.pbix" -Recurse
          foreach ($file in $pbixFiles) {
            Write-Host "Validating: $($file.Name)"
            # Add validation logic here
          }

- stage: Deploy_Test
  dependsOn: Build
  condition: eq(variables['Build.SourceBranch'], 'refs/heads/develop')
  jobs:
  - deployment: DeployToTest
    environment: 'PowerBI-Test'
    strategy:
      runOnce:
        deploy:
          steps:
          - task: PowerShell@2
            displayName: 'Deploy to Test Workspace'
            inputs:
              script: |
                Connect-PowerBIServiceAccount -ServicePrincipal -Credential $cred
                $workspace = Get-PowerBIWorkspace -Name "$(TestWorkspaceName)"
                Import-PowerBIDataflow -Path "$(Build.SourcesDirectory)/Reports/" -WorkspaceId $workspace.Id

- stage: Deploy_Production
  dependsOn: Deploy_Test
  condition: eq(variables['Build.SourceBranch'], 'refs/heads/main')
  jobs:
  - deployment: DeployToProduction
    environment: 'PowerBI-Production'
    strategy:
      runOnce:
        deploy:
          steps:
          - task: PowerShell@2
            displayName: 'Deploy to Production Workspace'
            inputs:
              script: |
                # Production deployment with additional safeguards
                $confirmDeploy = Read-Host "Confirm production deployment (yes/no)"
                if ($confirmDeploy -eq "yes") {
                  # Deploy to production
                }
\`\`\`

### Monitoring and Alerting

#### Advanced Monitoring Dashboard
\`\`\`dax
-- System health metrics
Dataset Refresh Status = 
VAR FailedRefreshes = 
    CALCULATE(
        COUNTROWS(RefreshHistory),
        RefreshHistory[Status] = "Failed",
        RefreshHistory[Date] >= TODAY() - 7
    )
VAR TotalRefreshes = 
    CALCULATE(
        COUNTROWS(RefreshHistory),
        RefreshHistory[Date] >= TODAY() - 7
    )
RETURN
    DIVIDE(FailedRefreshes, TotalRefreshes)

-- Usage analytics
Active Users Trend = 
CALCULATE(
    DISTINCTCOUNT(ActivityLog[UserId]),
    ActivityLog[Date] >= TODAY() - 30
)

-- Performance metrics
Average Query Duration = 
CALCULATE(
    AVERAGE(QueryLog[Duration]),
    QueryLog[Date] >= TODAY() - 1
)
\`\`\`

#### Automated Alerting System
\`\`\`python
import requests
import json
from datetime import datetime, timedelta

class PowerBIMonitoring:
    def __init__(self, tenant_id, client_id, client_secret):
        self.tenant_id = tenant_id
        self.client_id = client_id
        self.client_secret = client_secret
        self.access_token = self.get_access_token()
    
    def check_dataset_refresh_status(self, workspace_id, dataset_id):
        """Check if dataset refresh completed successfully"""
        url = f"https://api.powerbi.com/v1.0/myorg/groups/{workspace_id}/datasets/{dataset_id}/refreshes"
        headers = {"Authorization": f"Bearer {self.access_token}"}
        
        response = requests.get(url, headers=headers)
        if response.status_code == 200:
            refreshes = response.json()["value"]
            latest_refresh = refreshes[0] if refreshes else None
            
            if latest_refresh and latest_refresh["status"] == "Failed":
                self.send_alert(f"Dataset refresh failed: {dataset_id}")
                
    def monitor_query_performance(self, workspace_id):
        """Monitor query performance and alert on anomalies"""
        # Implementation for performance monitoring
        pass
        
    def send_alert(self, message):
        """Send alert to Teams/Email/SMS"""
        # Implementation for alert system
        pass
\`\`\`
      `,
      duration: '4 hours',
      difficulty: 'advanced',
      objectives: [
        'Design enterprise-scale Power BI architectures',
        'Implement comprehensive governance frameworks',
        'Plan and optimize Premium capacity utilization',
        'Build automated deployment pipelines',
        'Create advanced monitoring and alerting systems'
      ]
    }
    // Additional professional modules would continue here...
  ]
};
